/**
 * RegisterPage — FR-01
 * 登録成功 → 種別未選択なら /cert-select、選択済みなら /
 */

import { useState, useContext, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { registerUser } from '../api'
import { ApiClientError } from '../api/client'
import { AuthContext } from '../contexts/AuthContext'
import Spinner from '../components/Spinner'

export default function RegisterPage() {
  const { setUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== passwordConfirm) {
      setError('パスワードが一致しません')
      return
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください')
      return
    }

    setLoading(true)
    try {
      const user = await registerUser({ email, password })
      setUser(user)
      if (!user?.current_cert_type) {
        navigate('/cert-select', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError('登録に失敗しました。しばらく経ってから再試行してください。')
      }
    } finally {
      setLoading(false)
    }
  }

  const fields = [
    { id: 'register-email', testId: 'register-email', label: 'メールアドレス', type: 'email', value: email, onChange: setEmail, autoComplete: 'email' },
    { id: 'register-password', testId: 'register-password', label: 'パスワード（8文字以上）', type: 'password', value: password, onChange: setPassword, autoComplete: 'new-password' },
    { id: 'register-password-confirm', testId: 'register-password-confirm', label: 'パスワード（確認）', type: 'password', value: passwordConfirm, onChange: setPasswordConfirm, autoComplete: 'new-password' },
  ]

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-base px-6 py-10">
      <div className="w-full max-w-[400px] rounded-lg border border-border bg-surface p-8 shadow-card">
        <h1 className="mb-1 text-center text-[22px] font-bold tracking-tight text-text-primary">新規登録</h1>
        <p className="mb-7 text-center text-sm text-text-secondary">衛生管理者 試験対策アプリ</p>

        {error && (
          <div data-testid="register-error" role="alert" className="alert-error mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((field) => (
            <div key={field.id}>
              <label htmlFor={field.id} className="field-label">
                {field.label}
              </label>
              <input
                id={field.id}
                data-testid={field.testId}
                type={field.type}
                autoComplete={field.autoComplete}
                required
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                className="input-field"
              />
            </div>
          ))}

          <button type="submit" data-testid="register-submit" disabled={loading} className="btn-primary mt-2 w-full">
            {loading ? <Spinner label="登録中..." /> : '登録する'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-text-secondary">
          すでにアカウントをお持ちの方{' '}
          <Link to="/login" className="font-semibold text-accent hover:underline">
            ログイン
          </Link>
        </p>
      </div>
    </div>
  )
}
